import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2, Coins, FileText, Gauge, LayoutDashboard, LogOut, Menu as MenuIcon, ToggleLeft,
  LifeBuoy,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Mark } from '@/components/Mark';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { useAuth } from '@/Hooks/useAuth';
import { useAppDispatch, useAppSelector } from '@/Redux/store';
import { logout } from '@/Redux/Slices/authSlice';
import { closeMobileNav, toggleMobileNav } from '@/Redux/Slices/uiSlice';

interface NavItem {
  to: string;
  key: string;
  icon: LucideIcon;
}

/**
 * Grouped, because six flat rows is a list and two named groups is a map. An
 * operator arrives here knowing whether the problem is an *account* or the
 * *platform*, and the rail should answer that before they read a single label.
 */
const NAV: { group?: string; items: NavItem[] }[] = [
  { items: [{ to: '/', key: 'nav.overview', icon: LayoutDashboard }] },
  {
    group: 'platform.nav.groupAccounts',
    items: [
      { to: '/tenants', key: 'nav.tenants', icon: Building2 },
      { to: '/plans', key: 'nav.plans', icon: Gauge },
      { to: '/currencies', key: 'currency.title', icon: Coins },
    ],
  },
  {
    group: 'platform.nav.groupPlatform',
    items: [
      // The marketing site's own words. Under "Platform" rather than
      // "Accounts": tarabeeza.com belongs to the operator, not to any tenant.
      { to: '/content', key: 'nav.content', icon: FileText },
      { to: '/feature-flags', key: 'nav.featureFlags', icon: ToggleLeft },
      { to: '/support', key: 'nav.settings', icon: LifeBuoy },
    ],
  },
];

export default function Layout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const mobileNavOpen = useAppSelector((s) => s.ui.mobileNavOpen);

  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  return (
    <div className="flex h-dvh overflow-hidden">
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => dispatch(closeMobileNav())}
          aria-hidden
        />
      )}

      {/*
        The rail is the one place the brand appears in the shell, and it appears
        at 26px. This console manages every café on the platform; it is not one
        café's dashboard, so the mark identifies the tool rather than dressing it.
      */}
      <aside
        className={cn(
          'w-60 flex-col border-e border-[var(--border)] bg-[var(--rail)]',
          // Off-canvas below `md`, in the flow from `md` up. `start-0` rather
          // than `left-0` so it comes in from the correct edge under `dir=rtl`.
          'fixed inset-y-0 start-0 z-40',
          'md:static md:z-auto md:shrink-0',
          mobileNavOpen ? 'flex' : 'hidden md:flex',
        )}
      >
        <div className="flex h-12 shrink-0 items-center gap-2.5 border-b border-[var(--border)] px-3">
          <Mark size={26} />
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[13px] font-semibold tracking-tight">Tarabeeza</div>
            <div className="eyebrow truncate text-[var(--muted-foreground)]">
              {t('platform.appName')}
            </div>
          </div>
        </div>

        <nav className="scrollbar-thin flex-1 overflow-y-auto p-2">
          {NAV.map((section, index) => (
            <div key={section.group ?? 'root'} className={cn(index > 0 && 'mt-4')}>
              {section.group && (
                <div className="eyebrow px-2.5 pb-1 text-[var(--muted-foreground)]">
                  {t(section.group)}
                </div>
              )}
              <div className="space-y-0.5">
                {section.items.map(({ to, key, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === '/'}
                    onClick={() => dispatch(closeMobileNav())}
                    className={({ isActive }) =>
                      cn(
                        'relative flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
                        // The active marker is a 3px bar on the inline start, so
                        // it flips with `dir` and never needs an RTL override.
                        isActive
                          ? "bg-[var(--nav-active)] font-medium text-[var(--foreground)] before:absolute before:inset-y-1 before:start-0 before:w-[3px] before:rounded-full before:bg-[var(--marker)] before:content-['']"
                          : 'text-[var(--muted-foreground)] hover:bg-[var(--nav-hover)] hover:text-[var(--foreground)]',
                      )
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="truncate">{t(key)}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="eyebrow shrink-0 border-t border-[var(--border)] px-3 py-2 text-[var(--muted-foreground)]">
          {t('platform.scopeAllTenants')}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* `min-h-12` + wrap: the control cluster plus an operator's name does
            not fit one 320pt row, and a fixed-height header could only clip it. */}
        <header className="flex min-h-12 shrink-0 flex-wrap items-center justify-end gap-2 border-b border-[var(--border)] bg-[var(--card)] px-3 py-1.5">
          {/* `me-auto` keeps the rest end-aligned; at `md` the button is gone
              and the header is exactly what it was. */}
          <Button
            variant="ghost"
            size="icon"
            className="me-auto h-8 w-8 md:hidden"
            aria-label={t('common.menu', 'Menu')}
            aria-expanded={mobileNavOpen}
            onClick={() => dispatch(toggleMobileNav())}
          >
            <MenuIcon className="h-4 w-4" />
          </Button>
          <ThemeSwitcher />
          <LanguageSwitcher />
          {name && (
            <span className="hidden text-xs text-[var(--muted-foreground)] sm:inline">{name}</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={t('auth.logout')}
            aria-label={t('auth.logout')}
            onClick={() => void dispatch(logout())}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </header>

        <main className="scrollbar-thin flex-1 overflow-y-auto p-4 md:p-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
